/**
 * Magna Product Box
 * Handles: gallery carousel, thumbnail navigation, flavor selection,
 * subscription option switching, accordion, and add-to-cart with Loop selling plans.
 */

(function () {
  'use strict';

  const section = document.querySelector('[data-magna-product-box]');
  if (!section) return;

  const productJSON = section.parentElement.querySelector('[data-magna-product-json]');
  const product = productJSON ? JSON.parse(productJSON.textContent) : null;
  if (!product) return;

  /* ---------- GALLERY ---------- */
  const gallery = section.querySelector('[data-magna-gallery]');
  if (gallery) {
    const slides = gallery.querySelector('[data-magna-slides]');
    const slideEls = gallery.querySelectorAll('.magna-gallery__slide');
    const thumbs = gallery.querySelectorAll('.magna-gallery__thumb');
    const prevBtn = gallery.querySelector('[data-magna-prev]');
    const nextBtn = gallery.querySelector('[data-magna-next]');
    let currentSlide = 0;
    const totalSlides = slideEls.length;

    function goToSlide(index) {
      if (index < 0) index = totalSlides - 1;
      if (index >= totalSlides) index = 0;
      currentSlide = index;
      slides.style.transform = `translateX(-${currentSlide * 100}%)`;

      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === currentSlide);
      });

      // Scroll active thumb into view
      const activeThumb = thumbs[currentSlide];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    if (prevBtn) prevBtn.addEventListener('click', () => goToSlide(currentSlide - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goToSlide(currentSlide + 1));

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.dataset.thumbIndex, 10);
        goToSlide(index);
      });
    });

    // Touch/swipe support
    let touchStartX = 0;
    let touchEndX = 0;

    if (slides) {
      slides.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      slides.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const diff = touchStartX - touchEndX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) goToSlide(currentSlide + 1);
          else goToSlide(currentSlide - 1);
        }
      }, { passive: true });
    }

    // Expose for flavor selector
    window.magnaGalleryGoToSlide = goToSlide;
  }

  /* ---------- FLAVOR SELECTOR ---------- */
  const flavorBtns = section.querySelectorAll('.magna-flavor-selector__item');
  const variantInput = section.querySelector('[data-magna-variant-input]');
  const priceBadgeCurrent = section.querySelector('[data-magna-price-current]');
  const priceBadgeCompare = section.querySelector('[data-magna-price-compare]');
  const atcText = section.querySelector('[data-magna-atc-text]');
  const atcButton = section.querySelector('.magna-atc__button');

  function findVariantByFlavor(flavorHandle) {
    return product.variants.find((v) => {
      return v.options.some((opt) => opt.toLowerCase().replace(/\s+/g, '-') === flavorHandle);
    });
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(0);
  }

  function updateVariant(variant) {
    if (!variant) return;

    // Update hidden input
    if (variantInput) variantInput.value = variant.id;

    // Update price badge
    if (priceBadgeCurrent) priceBadgeCurrent.textContent = formatMoney(variant.price);
    if (priceBadgeCompare) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceBadgeCompare.textContent = formatMoney(variant.compare_at_price);
        priceBadgeCompare.style.display = '';
      } else {
        priceBadgeCompare.style.display = 'none';
      }
    }

    // Update ATC button availability
    if (atcButton) {
      atcButton.disabled = !variant.available;
    }
    if (atcText) {
      atcText.textContent = variant.available ? (section.dataset.atcText || 'TRY IT NOW') : 'Sold Out';
    }

    // Navigate gallery to variant's featured image
    if (variant.featured_media && window.magnaGalleryGoToSlide) {
      const slideEls = section.querySelectorAll('.magna-gallery__slide');
      slideEls.forEach((slide, index) => {
        if (slide.dataset.mediaId === String(variant.featured_media.id)) {
          window.magnaGalleryGoToSlide(index);
        }
      });
    }

    // Update URL without reload
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  flavorBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      // Update active state on all flavor selectors (there may be multiple)
      const allFlavorBtns = section.querySelectorAll('.magna-flavor-selector__item');
      allFlavorBtns.forEach((b) => b.classList.remove('is-active'));

      // Mark all buttons with same flavor as active
      const flavorHandle = btn.dataset.flavorHandle;
      allFlavorBtns.forEach((b) => {
        if (b.dataset.flavorHandle === flavorHandle) b.classList.add('is-active');
      });

      const variant = findVariantByFlavor(flavorHandle);
      if (variant) updateVariant(variant);
    });
  });

  /* ---------- SUBSCRIPTION OPTIONS ---------- */
  const subscriptionOptions = section.querySelectorAll('[data-magna-option]');
  const sellingPlanInput = section.querySelector('[data-magna-selling-plan-input]');
  const quantityInput = section.querySelector('[data-magna-quantity-input]');

  function updateBadgeColors() {
    subscriptionOptions.forEach((opt) => {
      const badge = opt.querySelector('.magna-subscription__option-badge');
      if (!badge) return;
      const isSelected = opt.classList.contains('is-selected');
      badge.style.backgroundColor = isSelected
        ? (badge.dataset.badgeActiveBg || badge.dataset.badgeBg || '#ffffff')
        : (badge.dataset.badgeBg || '#ffffff');
    });
  }

  subscriptionOptions.forEach((option) => {
    option.addEventListener('click', () => {
      // Deselect all
      subscriptionOptions.forEach((opt) => opt.classList.remove('is-selected'));
      // Select clicked
      option.classList.add('is-selected');

      // Update badge colors
      updateBadgeColors();

      // Update form inputs
      if (sellingPlanInput) {
        sellingPlanInput.value = option.dataset.sellingPlan || '';
      }
      if (quantityInput) {
        quantityInput.value = option.dataset.quantity || '1';
      }

      // If option specifies a variant override, use it
      if (option.dataset.variantId && variantInput) {
        variantInput.value = option.dataset.variantId;
      }
    });
  });

  // Set initial selling plan from default selected option
  const defaultOption = section.querySelector('[data-magna-option].is-selected');
  if (defaultOption && sellingPlanInput) {
    sellingPlanInput.value = defaultOption.dataset.sellingPlan || '';
    if (quantityInput) quantityInput.value = defaultOption.dataset.quantity || '1';
  }

  /* ---------- ADD TO CART ---------- */
  const form = section.querySelector('[data-magna-form]');
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();

      const submitBtn = form.querySelector('[type="submit"]');
      const spinner = submitBtn?.querySelector('.loading__spinner');

      if (submitBtn) {
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');
      }

      const formData = new FormData(form);
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', window.location.pathname);

      fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status) {
            console.error('Error adding to cart:', data.description);
            alert(data.description || 'Error adding to cart');
          } else {
            // Open cart drawer if available
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && cartDrawer.renderContents) {
              cartDrawer.renderContents(data);
            } else {
              window.location.href = '/cart';
            }
          }
        })
        .catch(() => {
          form.submit();
        })
        .finally(() => {
          if (submitBtn) {
            submitBtn.classList.remove('loading');
            submitBtn.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');
          }
        });
    });
  }

  /* ---------- ACCORDION ---------- */
  const accordionItems = section.querySelectorAll('[data-accordion-item]');
  accordionItems.forEach((item) => {
    const trigger = item.querySelector('.magna-accordion__trigger');
    const content = item.querySelector('.magna-accordion__content');

    if (trigger && content) {
      // Set initial state for open items
      if (item.classList.contains('is-open')) {
        content.style.maxHeight = content.scrollHeight + 'px';
      }

      trigger.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        if (isOpen) {
          content.style.maxHeight = content.scrollHeight + 'px';
          requestAnimationFrame(() => {
            content.style.maxHeight = '0px';
          });
          item.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('is-open');
          content.style.maxHeight = content.scrollHeight + 'px';
          trigger.setAttribute('aria-expanded', 'true');

          content.addEventListener('transitionend', function handler() {
            if (item.classList.contains('is-open')) {
              content.style.maxHeight = 'none';
            }
            content.removeEventListener('transitionend', handler);
          });
        }
      });
    }
  });
})();
